import { formatDateTime } from "@/lib/hooks/utils";
import { SalesRankListResponseDto } from "@/lib/interfaces/sales-ranks";
import { DataTableProps } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Award } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SalesRanksDataTable = ({
  data,
  pageCount,
  pagination,
  setPagination,
}: DataTableProps<SalesRankListResponseDto>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const columns: ColumnDef<SalesRankListResponseDto>[] = [
    {
      accessorKey: "title",
      header: () => (
        <div className="flex items-center">
          <p>Titel</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4" />
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    {
      accessorKey: "provision",
      header: () => (
        <div className="flex items-center">
          <p>Provision</p>
        </div>
      ),
      cell: ({ row }) => <div className="font-medium">{row.original.provision}%</div>,
    },
    {
      accessorKey: "createdAt",
      header: () => (
        <div className="flex items-center">
          <p>Erstellt am</p>
        </div>
      ),
      cell: ({ row }) => <div>{formatDateTime(row.original.createdAt)}</div>,
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  });

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`cursor-pointer hover:bg-gray-100 ${row.original.isArchive ? "bg-gray-200" : ""}`}
                  onClick={() => navigate(`/settings/sales-ranks/${row.original._id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Verkaufsränge gefunden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default SalesRanksDataTable;
